<template>
  <PortalForm
    :portal-type="customerType"
    :title="directoryType === 'claim' ? 'File a Claim' : 'Make a Payment'"
    :current-step="1"
    :steps="[]"
    :loading="false"
    :form-submitted="false"
    @back="goBack"
  >
    <div :class="[customerType === 'lender' ? 'card-lender' : 'card-customer']">
      <!-- Custom Toggle Switch -->
      <div class="mb-4 flex justify-center">
        <label class="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            class="sr-only peer"
            :checked="directoryType === 'claim'"
            @change="toggleDirectoryType"
          />
          <div
            class="h-6 w-11 rounded-full border border-gray-300 bg-slate-200 after:absolute after:top-1 after:left-1 after:bg-white after:border after:border-gray-300 after:h-4 after:w-4 after:rounded-full after:transition-all after:content-[''] peer-checked:after:translate-x-full"
            :class="props.customerType === 'customer' ? 'peer-checked:bg-green-300' : 'peer-checked:bg-blue-300'"
            aria-hidden="true"
          ></div>
        </label>
      </div>
      
      <p class="text-center text-gray-600 mb-5 mt-4">
        Find your carrier below to {{ directoryType === 'claim' ? 'file a claim' : 'make a payment' }}.
      </p>
      
      <div class="container px-0 md:px-[5%] mx-auto">
        <div class="w-full bg-white rounded shadow p-4">
          <!-- Search Input -->
          <input
            type="text"
            v-model="search"
            class="input-responsive mb-4 w-full"
            placeholder="Search for your carrier..."
            aria-label="Search for your carrier"
          />
          
          <!-- Loading State -->
          <div v-if="carrierStore.isLoading" class="flex justify-center py-8">
            <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
          
          <!-- Error State -->
          <div v-else-if="carrierStore.error" class="text-red-500 text-center py-4">
            {{ carrierStore.error }}
            <button 
              @click="carrierStore.fetchCarriers()" 
              class="mt-2 text-blue-500 underline"
            >
              Try Again
            </button>
          </div>
          
          <!-- Empty State -->
          <div 
            v-else-if="filteredCarriers.length === 0" 
            class="text-center py-8 text-gray-500"
          >
            <p v-if="search">No carriers found matching "{{ search }}"</p>
            <p v-else>No carriers available</p>
          </div>
          
          <!-- Carrier List -->
          <div v-else class="carriers-list overflow-auto" style="max-height: 400px">
            <div
              v-for="(carrier, index) in filteredCarriers"
              :key="index"
              class="grid grid-cols-1 items-center border-b py-6 gap-4"
              :class="{
                'sm:grid-cols-4': carrier.logoPath && carrier.logoPath.trim() !== '' && !imageErrors[carrier.id],
                'sm:grid-cols-3': !carrier.logoPath || carrier.logoPath.trim() === '' || imageErrors[carrier.id]
              }"
            >
              <!-- Only show logo column if it exists and loads successfully -->
              <div 
                v-if="carrier.logoPath && carrier.logoPath.trim() !== '' && !imageErrors[carrier.id]"
                class="sm:col-span-1 flex justify-center items-center"
              >
                <img
                  :src="carrier.logoPath"
                  class="max-w-[150px] sm:max-w-[180px] max-h-[80px] object-contain mx-auto"
                  :alt="`${carrier.name} Logo`"
                  @error="handleImageError(carrier.id)"
                />
              </div>
              
              <!-- Carrier Info - adjusts span based on whether logo is shown -->
              <div
                :class="{
                  'sm:col-span-2': carrier.logoPath && carrier.logoPath.trim() !== '' && !imageErrors[carrier.id],
                  'sm:col-span-2': !carrier.logoPath || carrier.logoPath.trim() === '' || imageErrors[carrier.id]
                }"
                class="text-xs sm:text-sm py-4 text-center sm:text-left"
              >
                <p class="text-black text-lg font-bold mb-2">
                  {{ carrier.name }}
                </p>
                <template v-if="directoryType === 'claim'">
                  <p class="text-gray-700 mb-1 flex flex-col sm:flex-row sm:items-center">
                    <span class="font-medium sm:w-32">Claims Phone:</span>
                    <a
                      :href="carrierStore.getFormattedPhoneLink(carrier, 'claims')"
                      :class="{
                        'text-red-600 underline': carrier.claimsPhone,
                        'text-gray-500': !carrier.claimsPhone
                      }"
                    >
                      {{ carrierStore.getFormattedPhoneDisplay(carrier, 'claims') }}
                    </a>
                  </p>
                </template>
                <template v-else>
                  <p class="text-gray-700 mb-1 flex flex-col sm:flex-row sm:items-center">
                    <span class="font-medium sm:w-32">Billing Phone:</span>
                    <a
                      :href="carrierStore.getFormattedPhoneLink(carrier, 'billing')"
                      :class="{
                        'text-red-600 underline': carrier.billingPhone,
                        'text-gray-500': !carrier.billingPhone
                      }"
                    >
                      {{ carrierStore.getFormattedPhoneDisplay(carrier, 'billing') }}
                    </a>
                  </p>
                  <div class="text-gray-700 mb-1 flex flex-col sm:flex-row">
                    <span class="font-medium sm:w-32 sm:mt-1">Payment Address:</span>
                    <span
                      :class="{
                        'text-red-600': carrier.overnightPaymentAddress,
                        'text-gray-500': !carrier.overnightPaymentAddress,
                      }"
                      class="sm:flex-1"
                    >
                      {{
                        carrier.overnightPaymentAddress || 'Not Available'
                      }}
                    </span>
                  </div>
                </template>
                <p class="text-gray-700 mb-1 flex flex-col sm:flex-row sm:items-center">
                  <span class="font-medium sm:w-32">Website:</span>
                  <a
                    :href="carrier.website ? carrier.website : '#'"
                    :class="{
                      'text-red-600 underline': carrier.website,
                      'text-gray-500': !carrier.website,
                    }"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ carrier.website || 'Not Available' }}
                  </a>
                </p>
              </div>
              
              <!-- Action Button -->
              <div :class="{'sm:col-span-1': true}" class="flex justify-center items-center">
                <a
                  :href="
                    (directoryType === 'claim'
                      ? carrier.claimsUrl
                      : carrier.paymentUrl) || '#'
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                  :class="[
                    (
                      directoryType === 'claim'
                        ? carrier.claimsUrl
                        : carrier.paymentUrl
                    )
                      ? customerType === 'lender' 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-400 text-gray-300 cursor-not-allowed',
                    'rounded-lg px-4 py-3 transition-colors w-full text-center block font-medium',
                  ]"
                  :disabled="
                    !(directoryType === 'claim'
                      ? carrier.claimsUrl
                      : carrier.paymentUrl)
                  "
                  :aria-disabled="
                    !(directoryType === 'claim'
                      ? carrier.claimsUrl
                      : carrier.paymentUrl)
                  "
                >
                  {{ directoryType === 'claim' ? 'File a Claim' : 'Make a Payment' }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PortalForm>
  
  <!-- Claims Education Dialog -->
  <ClaimEducationDialog
    v-model="showDialog"
    :customer-type="customerType"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue';
import { useRouter } from 'vue-router';
import PortalForm from '@/components/common/PortalForm.vue';
import ClaimEducationDialog from './ClaimEducationDialog.vue';
import { useCarrierDirectoryStore } from '@/stores/carrierDirectory';

// Props
const props = defineProps({
  customerType: {
    type: String,
    default: 'customer',
    validator: (value) => ['lender', 'customer'].includes(value)
  },
  initialType: {
    type: String,
    default: 'payment', // 'payment' or 'claim'
    validator: (value) => ['payment', 'claim'].includes(value)
  }
});

// Setup store and router
const carrierStore = useCarrierDirectoryStore();
const router = useRouter();

// Reactive state
const search = ref('');
const directoryType = ref(props.initialType);
const showDialog = ref(directoryType.value === 'claim');
const imageErrors = reactive({});

// Computed properties
const filteredCarriers = computed(() => {
  return carrierStore.getFilteredCarriers(search.value);
});

// Toggle between payment and claim modes
const toggleDirectoryType = () => {
  directoryType.value = directoryType.value === 'payment' ? 'claim' : 'payment';
  showDialog.value = directoryType.value === 'claim';
};

// Watch for external prop changes to adjust internal state
watch(
  () => props.initialType,
  (newVal) => {
    directoryType.value = newVal;
    showDialog.value = newVal === 'claim';
  },
);

// Navigation handler
const goBack = () => {
  router.push({ name: 'customerServicePage' });
};

// Fetch carriers on component mount
onMounted(() => {
  carrierStore.fetchCarriers();
});

// Image error handler to hide broken images
const handleImageError = (carrierId) => {
  imageErrors[carrierId] = true;
};
</script>

<style scoped>
.carriers-list {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.carriers-list::-webkit-scrollbar {
  width: 8px;
}

.carriers-list::-webkit-scrollbar-track {
  background: #f7fafc;
  border-radius: 4px;
}

.carriers-list::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 4px;
  border: 2px solid #f7fafc;
}
</style> 